<template>
  <patternlib-textinput
    :label="$t('serialNumber.additionalInformation')"
    additional-info-position="top"
    class="patternlibTextinputSerialnumber"
    :placeholder="$t('serialNumber.placeholder')"
    :show-copy-icon="false"
    input
    :status="
      serialNumberInputStatus == 'warning' ? 'default' : serialNumberInputStatus
    "
    size="L"
    :maxlength="12"
  >
    <div slot="info-message" v-if="serialNumberInputStatus == 'default'">
      {{ $t('serialNumber.validationRules.information') }}
    </div>
    <div slot="error-message" v-else-if="serialNumberInputStatus == 'error'">
      {{ $t('serialNumber.validationRules.error') }}
    </div>
    <div slot="info-message" v-else-if="serialNumberInputStatus == 'warning'">
      {{ $t('serialNumber.validationRules.warning') }}
    </div>
    <div
      slot="success-message"
      v-else-if="serialNumberInputStatus == 'success'"
    >
      {{ $t('serialNumber.validationRules.success') }}
    </div>
  </patternlib-textinput>
</template>

<script setup lang="ts">
  import type {
    LhChange,
    PatternlibTextinputCustomEvent
  } from '@liebherr/patternlib/dist/types/components';
  import { computed, onMounted, ref, watch } from 'vue';

  /* Parameters */
  const serialNumber = ref<string>('');

  const serialNumberInputStatus = computed(() =>
    validateSerialNumber(serialNumber.value)
  );

  watch(serialNumber, () => {
    emit('status-changed', serialNumberInputStatus.value);
    emit('serialnumber-changed', serialNumber.value.replace(/\D/g, ''));
  });

  /* Methods */
  const emit = defineEmits<{
    (e: 'status-changed', value: string): void;
    (e: 'serialnumber-changed', value: string): void;
  }>();

  const validateSerialNumber = (value: string) => {
    const checkDigit = (value: string) => {
      const checkSum1 =
        parseInt(value[0]) +
        parseInt(value[2]) +
        parseInt(value[4]) +
        parseInt(value[6]);
      const checkSum2 =
        parseInt(value[1]) +
        parseInt(value[3]) +
        parseInt(value[5]) +
        parseInt(value[7]);
      const checkSum3 = 10 - ((checkSum1 + 3 * checkSum2) % 10);
      return checkSum3 === 10 ? 0 : checkSum3;
    };

    const valueDigits = value.replace(/\D/g, '');
    if (valueDigits.length > 0 && valueDigits.length < 9) return 'warning';
    else if (valueDigits.length == 0) return 'default';
    return parseInt(valueDigits[8]) === checkDigit(valueDigits)
      ? 'success'
      : 'error';
  };

  const formatInput = (input: string, tb: HTMLPatternlibTextinputElement) => {
    const inputText = input.replace(/[^0-9]/g, '').slice(0, 9);
    const formattedValue = inputText.replace(
      /(\d{2})(\d{0,3})(\d{0,3})(\d?)/,
      (_match, p1, p2, p3, p4) => {
        let result = p1;
        if (p2) result += '.' + p2;
        if (p3) result += '.' + p3;
        if (p4) result += '.' + p4;
        return result;
      }
    );

    tb.value = formattedValue;
    serialNumber.value = formattedValue;

    return inputText.length;
  };

  const setSerialNumberManually = (serialNumberValue: string) => {
    const patternlibTextinput =
      document.querySelector<HTMLPatternlibTextinputElement>(
        '.patternlibTextinputSerialnumber'
      );

    patternlibTextinput?.dispatchEvent(
      new CustomEvent<LhChange>('lhChange', {
        detail: {
          value: serialNumberValue
        }
      })
    );
  };

  onMounted(() => {
    const patternlibTextinput =
      document.querySelector<HTMLPatternlibTextinputElement>(
        '.patternlibTextinputSerialnumber'
      );

    patternlibTextinput?.addEventListener(
      'lhChange',
      (e: PatternlibTextinputCustomEvent<LhChange>) => {
        const length = formatInput(
          e.detail.value.toString(),
          patternlibTextinput
        );
        if (length === 9) {
          validateSerialNumber(serialNumber.value);
        }
      }
    );
  });

  defineExpose({
    setSerialNumberManually
  });
</script>

<style scoped></style>
